import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { SortByEnum } from "build-ts/enums";
import { AppIcons } from "constants/icons";

type SortOption = "name_asc" | "name_desc" | "created_asc" | "created_desc" | "modified_asc" | "modified_desc";

const SORT_OPTION_LABELS: Record<SortOption, string> = {
  name_asc: "Name (A–Z)",
  name_desc: "Name (Z–A)",
  created_asc: "Created (oldest first)",
  created_desc: "Created (newest first)",
  modified_asc: "Last modified (oldest first)",
  modified_desc: "Last modified (newest first)",
};

const SORT_SELECT_OPTIONS = Object.entries(SORT_OPTION_LABELS) as [SortOption, string][];

function filterBySearchQuery(list: Header[], search: string): Header[] {
  const q = search.trim().toLowerCase();
  if (!q) return list;
  return list.filter(h => (h.name ?? "").toLowerCase().includes(q));
}

function sortByName(list: Header[], dir: SortByEnum): Header[] {
  const mult = dir === SortByEnum.ASCENDING ? 1 : -1;
  return [...list].sort(
    (a, b) => mult * String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, { sensitivity: "base" })
  );
}

function sortByLastModified(list: Header[], dir: SortByEnum): Header[] {
  const mult = dir === SortByEnum.ASCENDING ? 1 : -1;
  return [...list].sort(
    (a, b) => mult * ((Number(a.last_modified_date_ms) || 0) - (Number(b.last_modified_date_ms) || 0))
  );
}

function sortByCreatedDate(list: Header[], dir: SortByEnum): Header[] {
  const mult = dir === SortByEnum.ASCENDING ? 1 : -1;
  return [...list].sort((a, b) => mult * ((Number(a.created_date_ms) || 0) - (Number(b.created_date_ms) || 0)));
}

function filterByModel(list: Header[], modelId: string): Header[] {
  if (!modelId) return list;
  return list.filter(h => h.owner_id === modelId);
}

export interface ConfigurationListFilterControlProps {
  headerList: Header[];
  /** Drives whether the model filter is shown — only "metric" shows it */
  type: string;
  /** Required when type === "metric" to populate the model filter dropdown */
  modelList?: Header[];
  searchQuery: string;
  splitDetailOpen: boolean;
  onChange: (filteredList: Header[]) => void;
  /** Override the default sort option (default: "modified_desc") */
  defaultSortOption?: SortOption;
}

const ConfigurationListFilterControl: React.FC<ConfigurationListFilterControlProps> = ({
  headerList,
  type,
  modelList = [],
  searchQuery,
  splitDetailOpen,
  onChange,
  defaultSortOption = "modified_desc",
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [sortOption, setSortOption] = useState<SortOption>(defaultSortOption);
  const [modelFilterId, setModelFilterId] = useState<string>("");

  const showModelFilter = type === "metric";

  useEffect(() => {
    setSortOption(defaultSortOption);
    setModelFilterId("");
  }, [type]);

  useEffect(() => {
    let result = filterBySearchQuery(headerList, searchQuery);
    if (showModelFilter && modelFilterId) {
      result = filterByModel(result, modelFilterId);
    }
    switch (sortOption) {
      case "name_asc":
        result = sortByName(result, SortByEnum.ASCENDING);
        break;
      case "name_desc":
        result = sortByName(result, SortByEnum.DESCENDING);
        break;
      case "created_asc":
        result = sortByCreatedDate(result, SortByEnum.ASCENDING);
        break;
      case "created_desc":
        result = sortByCreatedDate(result, SortByEnum.DESCENDING);
        break;
      case "modified_asc":
        result = sortByLastModified(result, SortByEnum.ASCENDING);
        break;
      case "modified_desc":
        result = sortByLastModified(result, SortByEnum.DESCENDING);
        break;
    }
    onChange(result);
  }, [headerList, searchQuery, sortOption, modelFilterId, showModelFilter]);

  const filtersActive = sortOption !== defaultSortOption || Boolean(modelFilterId);

  const modelsSorted = [...modelList].sort((a, b) =>
    String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, { sensitivity: "base" })
  );

  const sortLabel = SORT_OPTION_LABELS[sortOption];
  const summary = showModelFilter
    ? modelFilterId
      ? `${sortLabel} · ${modelList.find(m => m.id === modelFilterId)?.name?.trim() || "Model"}`
      : `${sortLabel} · All models`
    : sortLabel;

  const showSummaryTrigger = splitDetailOpen || filtersActive;

  const summaryButton = (
    <Button
      size={splitDetailOpen ? "xs" : "sm"}
      fontWeight="600"
      leftIcon={
        !splitDetailOpen ? <Icon as={AppIcons.filter} boxSize={3.5} flexShrink={0} color="purple.700" /> : undefined
      }
      rightIcon={<Icon as={AppIcons.chevronDown} boxSize={3} flexShrink={0} color="purple.700" />}
      minW={0}
      flex={splitDetailOpen ? 1 : "0 1 auto"}
      maxW={splitDetailOpen ? "100%" : "min(700px, 75vw)"}
      px={3}
      overflow="hidden"
      aria-label="Filter and sort"
      bg={filtersActive ? "purple.50" : "white"}
      variant="outline"
      borderColor="purple.700"
      borderWidth="2px"
      color="purple.700"
      _hover={{ bg: "purple.50", borderColor: "purple.700", color: "purple.800" }}
      _active={{ bg: "purple.100" }}
    >
      <Box as="span" flex="1" minW={0} overflow="hidden" textAlign="left">
        <Text
          noOfLines={1}
          mb={0}
          fontSize={splitDetailOpen ? "xs" : "sm"}
          fontWeight={filtersActive ? "700" : "600"}
          color="purple.800"
          letterSpacing="tight"
        >
          {summary}
        </Text>
      </Box>
    </Button>
  );

  return (
    <Popover placement="bottom-end" closeOnBlur isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        {showSummaryTrigger ? (
          summaryButton
        ) : (
          <IconButton
            aria-label="Filter and sort"
            icon={<Icon as={AppIcons.filter} boxSize={4} />}
            size="sm"
            flexShrink={0}
            cursor="pointer"
            variant="outline"
            borderColor="purple.700"
            borderWidth="2px"
            color="purple.700"
            _hover={{ bg: "purple.50", borderColor: "purple.700", color: "purple.800" }}
            _active={{ bg: "purple.100" }}
          />
        )}
      </PopoverTrigger>
      <PopoverContent w="280px" borderColor="gray.200" boxShadow="lg">
        <PopoverArrow />
        <PopoverBody py={3} px={3}>
          <Box as="form" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
            <FormControl mb={showModelFilter ? 3 : 0}>
              <FormLabel fontSize="xs" fontWeight="700" color="gray.600" mb={1}>
                Sort by
              </FormLabel>
              <Select
                size="sm"
                bg="white"
                value={sortOption}
                onChange={e => {
                  setSortOption((e.target.value as SortOption) || defaultSortOption);
                  onClose();
                }}
                _focus={{
                  borderColor: "purple.600",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)",
                }}
              >
                {SORT_SELECT_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormControl>
            {showModelFilter && (
              <FormControl mb={0}>
                <FormLabel fontSize="xs" fontWeight="700" color="gray.600" mb={1}>
                  Data model
                </FormLabel>
                <Select
                  size="sm"
                  bg="white"
                  value={modelFilterId}
                  onChange={e => {
                    setModelFilterId(e.target.value);
                    onClose();
                  }}
                  _focus={{
                    borderColor: "purple.600",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)",
                  }}
                >
                  <option value="">All models</option>
                  {modelsSorted.map(m => (
                    <option key={m.id} value={m.id ?? ""}>
                      {m.name ?? m.id}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ConfigurationListFilterControl;
